import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Mail,
  Users,
  FileText,
  Inbox,
  MessageSquare,
  ListChecks,
  Settings,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Contact,
  Send,
  FileCode,
  MessageCircle,
  Cog,
  Smartphone,
  BarChart3,
  MailOpen,
  Zap,
} from 'lucide-react'
import { useUIStore } from '@stores/ui'
import { cn } from '@lib/utils'

// Veles Logo Component - Icon only for sidebar
const VelesLogo = ({ collapsed }: { collapsed: boolean }) => (
  <div className="flex items-center gap-3 overflow-hidden">
    <div className="relative flex-shrink-0">
      <img
        src="/veles-icon.svg"
        alt="Veles"
        className="h-10 w-10 relative"
      />
    </div>
    <AnimatePresence>
      {!collapsed && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.2 }}
          className="flex flex-col min-w-0"
        >
          <span className="text-lg font-bold bg-gradient-to-r from-white via-violet-100 to-violet-200 bg-clip-text text-transparent leading-tight">
            VelesMass
          </span>
          <span className="text-[10px] text-violet-400/60 font-medium tracking-wider uppercase">
            Enterprise
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
)

// Navigation items configuration
const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', description: 'Overview & analytics' },
  { to: '/campaigns', icon: Mail, label: 'Campaigns', description: 'Email campaigns' },
  { to: '/instant-send', icon: Zap, label: 'Instant Send', description: 'One-off bulk send' },
  { to: '/inbox', icon: MailOpen, label: 'Inbox', description: 'Email responses' },
  { to: '/tracking', icon: BarChart3, label: 'Tracking', description: 'Responder tracking' },
  { to: '/clients', icon: Users, label: 'Clients', description: 'Contact management' },
  { to: '/templates', icon: FileText, label: 'Templates', description: 'Email templates' },
  { to: '/mailboxes', icon: Inbox, label: 'Mailboxes', description: 'SMTP configuration' },
]

const whatsappSubItems = [
  { to: '/whatsapp/inbox', icon: MessageCircle, label: 'Inbox' },
  { to: '/whatsapp/contacts', icon: Contact, label: 'Contacts' },
  { to: '/whatsapp/campaigns', icon: Send, label: 'Campaigns' },
  { to: '/whatsapp/templates', icon: FileCode, label: 'Templates' },
  { to: '/whatsapp/mailboxes', icon: Smartphone, label: 'Connections' },
  { to: '/whatsapp/settings', icon: Cog, label: 'Settings' },
]

const bottomNavItems = [
  { to: '/queue', icon: ListChecks, label: 'Queue', description: 'Job queue status' },
  { to: '/settings', icon: Settings, label: 'Settings', description: 'App configuration' },
]

// Animation variants
const sidebarVariants = {
  expanded: { width: 280 },
  collapsed: { width: 80 },
}

// Nav Item Component - Premium violet theme
const NavItem = ({
  item,
  collapsed,
  index,
  isWhatsApp = false,
}: {
  item: { to: string; icon: any; label: string; description?: string }
  collapsed: boolean
  index: number
  isWhatsApp?: boolean
}) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const location = useLocation()
  const isActive = location.pathname === item.to ||
    (item.to !== '/dashboard' && location.pathname.startsWith(item.to))

  return (
    <div
      className="relative"
      onMouseEnter={() => collapsed && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <NavLink
        to={item.to}
        className={cn(
          'group flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-300',
          isActive
            ? isWhatsApp
              ? 'bg-gradient-to-r from-emerald-500/20 to-green-500/15 text-emerald-400 border border-emerald-500/25'
              : 'bg-gradient-to-r from-violet-500/20 to-purple-500/15 text-violet-300 border border-violet-500/25'
            : 'text-slate-400 hover:bg-violet-500/10 hover:text-slate-200'
        )}
      >
        <div className="flex-shrink-0">
          <item.icon size={20} className={cn(
            'transition-colors duration-200',
            isActive
              ? isWhatsApp ? 'text-emerald-400' : 'text-violet-400'
              : 'text-slate-500 group-hover:text-violet-400'
          )} />
        </div>

        <AnimatePresence>
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="whitespace-nowrap overflow-hidden"
            >
              {item.label}
            </motion.span>
          )}
        </AnimatePresence>
      </NavLink>

      {/* Tooltip for collapsed state */}
      <AnimatePresence>
        {collapsed && showTooltip && (
          <motion.div
            initial={{ opacity: 0, x: 10, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50"
          >
            <div className="glass px-3 py-2 rounded-lg border border-violet-500/20">
              <p className="text-sm font-medium text-slate-200 whitespace-nowrap">
                {item.label}
              </p>
              {item.description && (
                <p className="text-xs text-slate-500">
                  {item.description}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUIStore()
  const location = useLocation()
  const [whatsappOpen, setWhatsappOpen] = useState(
    location.pathname.startsWith('/whatsapp')
  )

  const isWhatsappActive = location.pathname.startsWith('/whatsapp')

  return (
    <motion.aside
      variants={sidebarVariants}
      initial={false}
      animate={sidebarCollapsed ? 'collapsed' : 'expanded'}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={cn(
        'fixed left-0 top-0 z-40 h-screen',
        'glass flex flex-col border-r border-violet-500/10'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-violet-500/10">
        <VelesLogo collapsed={sidebarCollapsed} />

        <motion.button
          onClick={toggleSidebar}
          className="p-2 rounded-lg text-slate-500 hover:text-violet-400 hover:bg-violet-500/10 transition-all duration-200"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Toggle sidebar"
        >
          {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </motion.button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 flex flex-col py-4 px-3 overflow-y-auto scrollbar-hide">
        {/* Main nav items */}
        <div className="space-y-1">
          {navItems.map((item, index) => (
            <NavItem
              key={item.to}
              item={item}
              collapsed={sidebarCollapsed}
              index={index}
            />
          ))}
        </div>

        {/* WhatsApp Section */}
        <div className="mt-4 pt-4 border-t border-violet-500/10">
          {sidebarCollapsed ? (
            <NavItem
              item={{ to: '/whatsapp/inbox', icon: MessageSquare, label: 'WhatsApp' }}
              collapsed={sidebarCollapsed}
              index={navItems.length}
              isWhatsApp
            />
          ) : (
            <div className="space-y-1">
              <motion.button
                onClick={() => setWhatsappOpen(!whatsappOpen)}
                className={cn(
                  'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-300',
                  isWhatsappActive
                    ? 'bg-gradient-to-r from-emerald-500/20 to-green-500/15 text-emerald-400 border border-emerald-500/25'
                    : 'text-slate-400 hover:bg-violet-500/10 hover:text-slate-200'
                )}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-3">
                  <MessageSquare size={20} className={isWhatsappActive ? 'text-emerald-400' : 'text-slate-500'} />
                  <span>WhatsApp</span>
                </div>
                <motion.div
                  animate={{ rotate: whatsappOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown size={16} />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {whatsappOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="ml-4 pl-4 border-l border-emerald-500/20 space-y-1 py-1">
                      {whatsappSubItems.map((item, index) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                              isActive
                                ? 'bg-emerald-500/15 text-emerald-400'
                                : 'text-slate-500 hover:bg-violet-500/10 hover:text-slate-300'
                            )
                          }
                        >
                          <item.icon size={16} />
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom nav items */}
        <div className="pt-4 border-t border-violet-500/10 space-y-1">
          {bottomNavItems.map((item, index) => (
            <NavItem
              key={item.to}
              item={item}
              collapsed={sidebarCollapsed}
              index={navItems.length + 1 + index}
            />
          ))}
        </div>
      </nav>

      {/* Footer - Version info */}
      <div className="flex-shrink-0 px-3 py-2 border-t border-violet-500/10">
        <AnimatePresence>
          {!sidebarCollapsed ? (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-[9px] text-violet-400/40 text-center font-medium truncate"
            >
              v2.0
            </motion.p>
          ) : (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-[9px] text-violet-400/40 text-center font-medium"
            >
              v2
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.aside>
  )
}
